/**
 * `src/spa/registry.ts` — turn the tenant's `resolve` payload into single-spa
 * registrations, backed by `System.import()` against the injected import map.
 */
import { registerApplication, type AppProps, type LifeCycles } from "single-spa";
import type { ImportMapEntry, ResolvedMfe } from "@/tenant/types";
import { routeUnderPrefix } from "@/nav/navFilter";

export function buildImportMap(entries: ImportMapEntry[]): { imports: Record<string, string> } {
  return { imports: Object.fromEntries(entries.map((e) => [e.key, e.url])) };
}

/** Append the `<script type="systemjs-importmap">` SystemJS reads before any `System.import()` call. */
export function injectImportMap(entries: ImportMapEntry[], doc: Document = document): void {
  const script = doc.createElement("script");
  script.type = "systemjs-importmap";
  script.textContent = JSON.stringify(buildImportMap(entries));
  doc.head.appendChild(script);
}

type MfeMatch = Pick<ResolvedMfe, "pathPrefix" | "matchMode" | "overlay">;

/** Overlays (comms, help-center) mount on every route; everything else matches its prefix (or exact path). */
export function activeWhenFor(mfe: MfeMatch): (location: Pick<Location, "pathname">) => boolean {
  if (mfe.overlay) return () => true;
  if (mfe.matchMode === "exact") {
    return (location) => location.pathname === mfe.pathPrefix;
  }
  return (location) => routeUnderPrefix(location.pathname, mfe.pathPrefix);
}

/**
 * `System.import` (populated by the `systemjs` side-effect import in
 * `main.ts`) resolves to the module's single-spa lifecycle object directly —
 * it is NOT itself a further loading function, so this loader's signature
 * matches single-spa's `(config: AppProps) => Promise<LifeCycles>` contract.
 */
function loadMfe(importKey: string): (config: AppProps) => Promise<LifeCycles> {
  return () => window.System.import<LifeCycles>(importKey);
}

/** Register every resolved module (and only those) as a single-spa application. */
export function registerTenantModules(modules: ResolvedMfe[]): void {
  for (const mfe of modules) {
    registerApplication({
      name: mfe.importKey,
      app: loadMfe(mfe.importKey),
      activeWhen: activeWhenFor(mfe),
    });
  }
}
